import { PlusOutlined } from '@ant-design/icons';
import { analytics, translate, TranslationEntity } from '@repo/utils';
import {
  MAX_BUCKET_COUNT,
} from 'advanced-filters/data/constants';
import { BASE_COMPARATOR_OPTIONS, type BaseOperatorType } from 'advanced-filters/data/options';
import {
  baseComparatorSelector,
  filterLevelsSelector,
  selectedLevelSelector,
} from 'advanced-filters/selectors/filter';
import {
  addFilterLevel,
  deleteBucket,
  setFilterOperator,
  setSelectedLevel,
} from 'advanced-filters/store/filter';
import { type AdvancedFilterButtonProps } from 'advanced-filters/types/advancedFilterButton';
import {
  Button, Card, Radio, type RadioChangeEvent, Tooltip, Typography,
} from 'antd';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AdvancedFilterButton from '../AdvancedFilterButton';
import BucketSelector from '../BucketSelector';
import styles from './FilterList.module.scss';

function BucketOperator() {
  const dispatch = useDispatch();
  const operator = useSelector(baseComparatorSelector);

  const onChange = useCallback((e: RadioChangeEvent) => {
    dispatch(setFilterOperator(e.target.value));
  }, [dispatch]);

  return (
    <div className={styles.operatorForm}>
      <Typography.Text type="secondary">
        {translate(`Search for #{${TranslationEntity.lead}} that match:`, [{
          pluralize: true,
        }])}
      </Typography.Text>

      <Radio.Group value={operator} onChange={onChange}>
        <Radio
          value="OR"
          className={clsx(operator === BASE_COMPARATOR_OPTIONS.or && styles.activeOperator)}
        >
          Any Bucket
        </Radio>
        <Radio
          value="AND"
          className={clsx(operator === BASE_COMPARATOR_OPTIONS.and && styles.activeOperator)}
        >
          All Buckets
        </Radio>
      </Radio.Group>
    </div>
  );
}

export default function FilterList({
  footerConfig,
}: {
  footerConfig: AdvancedFilterButtonProps[]
}) {
  const dispatch = useDispatch();
  const buckets = useSelector(filterLevelsSelector);
  const baseComparator = useSelector(baseComparatorSelector);
  const selectedLevel = useSelector(selectedLevelSelector);

  const handleAddBucket = useCallback(() => {
    if (buckets.length < MAX_BUCKET_COUNT) {
      analytics.click({
        click_type: 'add_advanced_filter_bucket',
        click_source: 'advanced_filter_drawer',
        click_text: 'Add Filter Bucket',
        custom: { current_bucket_count: buckets.length },
      });
      dispatch(addFilterLevel());
    }
  }, [dispatch, buckets.length]);

  const handleLevelClick = useCallback((level: number) => {
    dispatch(setSelectedLevel(level));
  }, [dispatch]);

  const handleOperatorChange = useCallback((level: number, operator: BaseOperatorType) => {
    dispatch(setFilterOperator({ level, operator }));
  }, [dispatch]);

  const handleDeleteBucket = useCallback((level: number) => {
    dispatch(deleteBucket({ level }));
  }, [dispatch]);

  return (
    <Card
      className={styles.container}
      title="Filter Buckets"
      extra={<BucketOperator />}
      classNames={{ body: styles.cardBody }}
    >
      <div className={styles.cardContent}>
        <BucketSelector
          buckets={buckets}
          baseComparator={baseComparator}
          selectedLevel={selectedLevel}
          onLevelClick={handleLevelClick}
          onOperatorChange={handleOperatorChange}
          onDeleteBucket={handleDeleteBucket}
        />
        <div className={styles.footer}>
          <Tooltip
            title={buckets.length >= MAX_BUCKET_COUNT ? `Maximum of ${MAX_BUCKET_COUNT} filter buckets allowed` : ''}
            placement="top"
          >
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddBucket}
              disabled={buckets.length >= MAX_BUCKET_COUNT}
            >
              Add Filter Bucket
            </Button>
          </Tooltip>
          <div className={styles.footerRight}>
            {footerConfig.map((config, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                <AdvancedFilterButton {...config} />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
